import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import GuestRoute from './components/GuestRoute';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import UserRoute from './components/UserRoute';
import PublicLayout from './layouts/PublicLayout';
import MaiaPublicLayout from './layouts/MaiaPublicLayout';
import AuthLayout from './layouts/AuthLayout';
import AppLayout from './layouts/AppLayout';
import Home from './pages/Home';
import MaiaLanding from './pages/MaiaLanding';
import WhatsAppLanding from './pages/WhatsAppLanding';
import ArticlesIndex from './pages/ArticlesIndex';
import ArticleShow from './pages/ArticleShow';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Login from './pages/Login';
import Register from './pages/Register';
import LeadCapture from './pages/LeadCapture';
import DashboardPlaceholder from './pages/Dashboard';
import AgentsList from './pages/Agents';
import ContactsList from './pages/Contacts';
import ContactDetail from './pages/Contacts/show';
import AgentEdit from './pages/Agents/edit';
import SubAgentsList from './pages/Agents/subAgents';
import KnowledgeBasesList from './pages/KnowledgeBases';
import KnowledgeBaseNew from './pages/KnowledgeBases/new';
import KnowledgeBaseShow from './pages/KnowledgeBases/show';
import McpServersList from './pages/McpServers';
import McpServerForm from './pages/McpServers/form';
import EdgeConnectorsPage from './pages/EdgeConnectors';
import OnboardingWizard from './pages/Onboarding';
import LlmCatalogAdmin from './pages/LlmCatalog';
import PromptTemplatesAdmin from './pages/PromptTemplates';
import PromptTemplateEdit from './pages/PromptTemplates/edit';
import WhatsappCatalogAdmin from './pages/WhatsappCatalog';
import InstagramCatalogAdmin from './pages/InstagramCatalog';
import AdminAgentsList from './pages/AdminAgents';
import AdminChannels from './pages/AdminChannels';
import AdminAccounts from './pages/AdminAccounts';
import ClientChannels from './pages/Channels';
import AdminContactsList from './pages/AdminContacts';
import AdminContactDetail from './pages/AdminContacts/show';
import ScheduledMessagesList from './pages/ScheduledMessages';
import ScheduledMessageShow from './pages/ScheduledMessages/show';
import ScheduledMessageEdit from './pages/ScheduledMessages/edit';
import AdminScheduledMessagesList from './pages/AdminScheduledMessages';
import AdminScheduledMessageShow from './pages/AdminScheduledMessages/show';
import AdminScheduledMessageEdit from './pages/AdminScheduledMessages/edit';
import InboundRoutersList from './pages/InboundRouters';
import InboundRouterForm from './pages/InboundRouters/form';
import InboundRouterGate from './components/InboundRouterGate';
import SelectAccount from './pages/SelectAccount';
import Team from './pages/Team';
import InviteAccept from './pages/InviteAccept';
import AgentAnalysisReportsList from './pages/AgentAnalysisReports';
import AgentAnalysisReportShow from './pages/AgentAnalysisReports/show';
import ContactSpaceConnections, {
  ContactSpaceEntry,
} from './pages/ContactSpace';

function readPublicRootMeta() {
  if (typeof document === 'undefined') {
    return { hydrateMaiaLanding: false, hydrateWhatsAppLanding: false };
  }

  const rootElement = document.getElementById('root');
  const prerenderedRoute = rootElement?.dataset.prerenderedRoute;
  const currentRoute =
    typeof window !== 'undefined'
      ? window.location.pathname.replace(/\/+$/, '') || '/'
      : '/';
  const hydrateMaiaLanding =
    prerenderedRoute === currentRoute && currentRoute === '/mentor-ia';
  const hydrateWhatsAppLanding =
    prerenderedRoute === currentRoute && currentRoute === '/agente-ia-whatsapp';

  return { hydrateMaiaLanding, hydrateWhatsAppLanding };
}

function App() {
  const { hydrateMaiaLanding, hydrateWhatsAppLanding } = readPublicRootMeta();

  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route index element={<Home />} />
            <Route path="plataforma" element={<Navigate to="/" replace />} />
            <Route
              path="agente-ia-whatsapp"
              element={<WhatsAppLanding prerender={hydrateWhatsAppLanding} />}
            />
            <Route path="artigos" element={<ArticlesIndex />} />
            <Route path="artigos/:slug" element={<ArticleShow />} />
            <Route path="privacidade" element={<PrivacyPolicy />} />
            <Route path="termos" element={<TermsOfService />} />
          </Route>

          <Route element={<MaiaPublicLayout />}>
            <Route path="mentor-ia" element={<MaiaLanding prerender={hydrateMaiaLanding} />} />
          </Route>

          <Route path="espaco/:token" element={<ContactSpaceEntry />} />
          <Route path="espaco/conexoes" element={<ContactSpaceConnections />} />

          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route
              path="select-account"
              element={<SelectAccount />}
            />
            <Route
              path="team"
              element={
                <UserRoute>
                  <Team />
                </UserRoute>
              }
            />
            <Route
              path="dashboard"
              element={
                <UserRoute>
                  <DashboardPlaceholder />
                </UserRoute>
              }
            />
            <Route
              path="agents"
              element={
                <UserRoute>
                  <AgentsList />
                </UserRoute>
              }
            />
            <Route
              path="agents/:agentId/sub-agents"
              element={
                <UserRoute>
                  <SubAgentsList />
                </UserRoute>
              }
            />
            <Route
              path="agents/:agentId/edit"
              element={
                <UserRoute>
                  <AgentEdit />
                </UserRoute>
              }
            />
            <Route
              path="channels"
              element={
                <UserRoute>
                  <ClientChannels />
                </UserRoute>
              }
            />
            <Route
              path="contacts"
              element={
                <UserRoute>
                  <ContactsList />
                </UserRoute>
              }
            />
            <Route
              path="contacts/:contactId"
              element={
                <UserRoute>
                  <ContactDetail />
                </UserRoute>
              }
            />
            <Route
              path="analysis-reports"
              element={
                <UserRoute>
                  <AgentAnalysisReportsList />
                </UserRoute>
              }
            />
            <Route
              path="analysis-reports/:reportId"
              element={
                <UserRoute>
                  <AgentAnalysisReportShow />
                </UserRoute>
              }
            />
            <Route
              path="knowledge-bases"
              element={
                <UserRoute>
                  <KnowledgeBasesList />
                </UserRoute>
              }
            />
            <Route
              path="knowledge-bases/new"
              element={
                <UserRoute>
                  <KnowledgeBaseNew />
                </UserRoute>
              }
            />
            <Route
              path="knowledge-bases/:id"
              element={
                <UserRoute>
                  <KnowledgeBaseShow />
                </UserRoute>
              }
            />
            <Route
              path="mcp-servers"
              element={
                <UserRoute>
                  <McpServersList />
                </UserRoute>
              }
            />
            <Route
              path="mcp-servers/new"
              element={
                <UserRoute>
                  <McpServerForm />
                </UserRoute>
              }
            />
            <Route
              path="mcp-servers/:id/edit"
              element={
                <UserRoute>
                  <McpServerForm />
                </UserRoute>
              }
            />
            <Route
              path="edge-connectors"
              element={
                <UserRoute>
                  <EdgeConnectorsPage />
                </UserRoute>
              }
            />
            <Route
              path="onboarding"
              element={
                <UserRoute>
                  <OnboardingWizard />
                </UserRoute>
              }
            />
            <Route
              path="inbound-routers"
              element={
                <UserRoute>
                  <InboundRouterGate>
                    <InboundRoutersList />
                  </InboundRouterGate>
                </UserRoute>
              }
            />
            <Route
              path="inbound-routers/new"
              element={
                <UserRoute>
                  <InboundRouterGate>
                    <InboundRouterForm />
                  </InboundRouterGate>
                </UserRoute>
              }
            />
            <Route
              path="inbound-routers/:id/edit"
              element={
                <UserRoute>
                  <InboundRouterGate>
                    <InboundRouterForm />
                  </InboundRouterGate>
                </UserRoute>
              }
            />
            <Route
              path="scheduled-messages"
              element={
                <UserRoute>
                  <ScheduledMessagesList />
                </UserRoute>
              }
            />
            <Route
              path="scheduled-messages/:scheduledMessageId"
              element={
                <UserRoute>
                  <ScheduledMessageShow />
                </UserRoute>
              }
            />
            <Route
              path="scheduled-messages/:scheduledMessageId/edit"
              element={
                <UserRoute>
                  <ScheduledMessageEdit />
                </UserRoute>
              }
            />
            <Route
              path="admin/agents"
              element={
                <AdminRoute>
                  <AdminAgentsList />
                </AdminRoute>
              }
            />
            <Route
              path="admin/accounts"
              element={
                <AdminRoute>
                  <AdminAccounts />
                </AdminRoute>
              }
            />
            <Route
              path="admin/channels"
              element={
                <AdminRoute>
                  <AdminChannels />
                </AdminRoute>
              }
            />
            <Route
              path="admin/contacts"
              element={
                <AdminRoute>
                  <AdminContactsList />
                </AdminRoute>
              }
            />
            <Route
              path="admin/contacts/:contactId"
              element={
                <AdminRoute>
                  <AdminContactDetail />
                </AdminRoute>
              }
            />
            <Route
              path="admin/scheduled-messages"
              element={
                <AdminRoute>
                  <AdminScheduledMessagesList />
                </AdminRoute>
              }
            />
            <Route
              path="admin/scheduled-messages/:scheduledMessageId"
              element={
                <AdminRoute>
                  <AdminScheduledMessageShow />
                </AdminRoute>
              }
            />
            <Route
              path="admin/scheduled-messages/:scheduledMessageId/edit"
              element={
                <AdminRoute>
                  <AdminScheduledMessageEdit />
                </AdminRoute>
              }
            />
            <Route
              path="llm-catalog"
              element={
                <AdminRoute>
                  <LlmCatalogAdmin />
                </AdminRoute>
              }
            />
            <Route
              path="prompt-templates"
              element={
                <AdminRoute>
                  <PromptTemplatesAdmin />
                </AdminRoute>
              }
            />
            <Route
              path="prompt-templates/:slug/edit"
              element={
                <AdminRoute>
                  <PromptTemplateEdit />
                </AdminRoute>
              }
            />
            <Route
              path="whatsapp-catalog"
              element={
                <AdminRoute>
                  <WhatsappCatalogAdmin />
                </AdminRoute>
              }
            />
            <Route
              path="instagram"
              element={
                <AdminRoute>
                  <InstagramCatalogAdmin />
                </AdminRoute>
              }
            />
          </Route>

          <Route element={<AuthLayout />}>
            <Route
              path="invite/:token"
              element={<InviteAccept />}
            />
            <Route
              path="login"
              element={
                <GuestRoute>
                  <Login />
                </GuestRoute>
              }
            />
            <Route
              path="lead"
              element={
                <GuestRoute>
                  <LeadCapture />
                </GuestRoute>
              }
            />
            <Route
              path="register"
              element={
                <GuestRoute>
                  <Register />
                </GuestRoute>
              }
            />
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
